import React, { useState, useMemo } from 'react';
import {
  LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import {
  Eye, Users, UserCheck, TrendingUp, Monitor, Smartphone, Tablet,
  Loader2, BarChart3, X, ChevronRight, Globe, FileText, Target, Share2
} from 'lucide-react';
import { countryName } from './countryNames';

// Palette drawn strictly from tailwind.config.js (primary / success / warning / error / gray).
const C = {
  primary: '#2563eb',
  primaryLight: '#3b82f6',
  primaryDark: '#1d4ed8',
  success: '#16a34a',
  warning: '#d97706',
  error: '#dc2626',
  gray: '#6b7280',
  grayLight: '#9ca3af',
  grid: '#f3f4f6',
  axis: '#9ca3af',
};

// Series colors for multi-slice charts — system tokens only.
const SERIES = [C.primary, C.success, C.warning, C.primaryDark, C.error, C.gray, C.primaryLight, C.grayLight];

const RANGE_OPTIONS = [
  { label: '7d', days: 7 },
  { label: '14d', days: 14 },
  { label: '30d', days: 30 },
  { label: '90d', days: 90 },
];

const DEVICE_ICONS = { desktop: Monitor, mobile: Smartphone, tablet: Tablet };

// How many rows each list card shows inline before overflowing into the modal.
const INLINE_ROWS = 6;

const nf = new Intl.NumberFormat('en');
const fmtNum = (n) => (Number.isFinite(Number(n)) ? nf.format(Number(n)) : '—');

const safeStr = (v) => String(v ?? '').trim();

/** Turn a stored page_url into a readable path without throwing. */
const prettyPath = (url) => {
  const raw = safeStr(url);
  if (!raw) return '—';
  try {
    const u = raw.startsWith('http') ? new URL(raw) : new URL('https://placeholder.local' + (raw.startsWith('/') ? raw : '/' + raw));
    return (u.pathname || '/') + (u.search || '');
  } catch {
    return raw;
  }
};

/** Referrer label: hostname when it is a URL, otherwise the stored label (e.g. "direct"). */
const prettySource = (src) => {
  const raw = safeStr(src);
  if (!raw) return 'Direct';
  try {
    if (raw.startsWith('http')) return new URL(raw).hostname.replace(/^www\./, '');
  } catch { /* fall through to raw */ }
  return raw;
};

const fmtDateShort = (d) => {
  try {
    const dt = new Date(d);
    if (Number.isNaN(dt.getTime())) return safeStr(d);
    return dt.toLocaleDateString('en', { month: 'short', day: 'numeric' });
  } catch { return safeStr(d); }
};

const fmtDateLong = (d) => {
  try {
    const dt = new Date(d);
    if (Number.isNaN(dt.getTime())) return safeStr(d);
    return dt.toLocaleDateString('en', { weekday: 'short', month: 'short', day: 'numeric' });
  } catch { return safeStr(d); }
};

// ── Stat card ────────────────────────────────────────────────
const STAT_STYLES = {
  primary: { bg: 'bg-primary-50', fg: 'text-primary-600' },
  success: { bg: 'bg-success-50', fg: 'text-success-600' },
  warning: { bg: 'bg-warning-50', fg: 'text-warning-600' },
  gray: { bg: 'bg-gray-100', fg: 'text-gray-600' },
};

const StatCard = ({ icon: Icon, label, value, tone = 'primary', hint }) => {
  const s = STAT_STYLES[tone] || STAT_STYLES.primary;
  return (
    <div className="bg-white border border-gray-200 rounded-lg px-4 py-3.5 flex items-center gap-3 min-w-0">
      <div className={`w-9 h-9 rounded-md flex items-center justify-center flex-shrink-0 ${s.bg}`}>
        <Icon className={`w-4 h-4 ${s.fg}`} strokeWidth={2} />
      </div>
      <div className="min-w-0">
        <p className="text-xl font-semibold text-gray-900 leading-tight tabular-nums truncate">{value}</p>
        <p className="text-[11px] text-gray-500 leading-tight truncate">{label}</p>
        {hint && <p className="text-[11px] text-gray-400 leading-tight truncate">{hint}</p>}
      </div>
    </div>
  );
};

// ── Card shell with an optional "view all" action ────────────
const Card = ({ title, icon: Icon, count, onViewAll, children, className = '' }) => (
  <div className={`bg-white border border-gray-200 rounded-lg flex flex-col min-w-0 ${className}`}>
    <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-gray-100 flex-shrink-0">
      <h4 className="text-sm font-semibold text-gray-800 flex items-center gap-2 min-w-0">
        {Icon && <Icon className="w-4 h-4 text-gray-400 flex-shrink-0" strokeWidth={2} />}
        <span className="truncate">{title}</span>
        {count > 0 && (
          <span className="text-[11px] font-normal text-gray-400 flex-shrink-0">({fmtNum(count)})</span>
        )}
      </h4>
      {onViewAll && (
        <button
          onClick={onViewAll}
          className="text-[11px] font-medium text-primary-600 hover:text-primary-700 flex items-center gap-0.5 flex-shrink-0 transition-colors"
        >
          View all
          <ChevronRight className="w-3 h-3" />
        </button>
      )}
    </div>
    <div className="p-4 flex-1 min-w-0">{children}</div>
  </div>
);

// ── Modal for full lists (keeps cards compact, nothing gets cut off) ──
const OverflowModal = ({ title, icon: Icon, rows, onClose }) => {
  if (!rows) return null;
  const max = rows.reduce((m, r) => Math.max(m, Number(r.value) || 0), 0) || 1;
  return (
    <div
      className="fixed inset-0 z-50 bg-gray-900/40 flex items-center justify-center p-4"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="bg-white rounded-xl shadow-strong w-full max-w-xl max-h-[80vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-gray-200 flex-shrink-0">
          <h4 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
            {Icon && <Icon className="w-4 h-4 text-gray-400" strokeWidth={2} />}
            {title}
            <span className="text-xs font-normal text-gray-400">({fmtNum(rows.length)})</span>
          </h4>
          <button
            onClick={onClose}
            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="overflow-y-auto px-5 py-3">
          <table className="w-full text-sm">
            <tbody>
              {rows.map((r, i) => (
                <tr key={`${r.label}-${i}`} className="border-b border-gray-50 last:border-0">
                  <td className="py-2 pr-3 text-[11px] text-gray-400 tabular-nums w-8 align-middle">{i + 1}</td>
                  <td className="py-2 pr-3 align-middle min-w-0">
                    <p className="text-gray-800 truncate" title={r.title || r.label}>{r.label}</p>
                    <div className="mt-1 h-1 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full"
                        style={{ width: `${((Number(r.value) || 0) / max) * 100}%`, backgroundColor: C.primary }}
                      />
                    </div>
                  </td>
                  <td className="py-2 text-right text-gray-900 font-medium tabular-nums whitespace-nowrap align-middle">
                    {fmtNum(r.value)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// ── CSS bar rows: no recharts, so labels never overlap or shrink ──
const BarRows = ({ rows, color = C.primary, emptyText = 'No data yet' }) => {
  if (!rows || rows.length === 0) {
    return <p className="text-xs text-gray-400 py-6 text-center">{emptyText}</p>;
  }
  const max = rows.reduce((m, r) => Math.max(m, Number(r.value) || 0), 0) || 1;
  return (
    <div className="space-y-2.5">
      {rows.map((r, i) => (
        <div key={`${r.label}-${i}`} className="min-w-0">
          <div className="flex items-baseline justify-between gap-3 mb-1">
            <span className="text-xs text-gray-700 truncate min-w-0" title={r.title || r.label}>
              {r.label}
            </span>
            <span className="text-xs font-medium text-gray-900 tabular-nums flex-shrink-0">
              {fmtNum(r.value)}
            </span>
          </div>
          <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
            <div
              className="h-full rounded-full transition-all duration-300"
              style={{ width: `${((Number(r.value) || 0) / max) * 100}%`, backgroundColor: color }}
            />
          </div>
        </div>
      ))}
    </div>
  );
};

// ── Donut with a side legend (no inline slice labels = no overlap) ──
const Donut = ({ rows, colors = SERIES, centerLabel, centerValue }) => {
  const total = rows.reduce((s, r) => s + (Number(r.value) || 0), 0);
  if (total <= 0) {
    return <p className="text-xs text-gray-400 py-6 text-center">No data yet</p>;
  }
  return (
    <div className="flex items-center gap-4 min-w-0">
      {/* Fixed-size chart box so the donut can never collapse */}
      <div className="relative flex-shrink-0" style={{ width: 132, height: 132 }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
            <Pie
              data={rows}
              dataKey="value"
              nameKey="label"
              cx="50%"
              cy="50%"
              innerRadius={44}
              outerRadius={64}
              paddingAngle={rows.length > 1 ? 2 : 0}
              stroke="none"
              isAnimationActive={false}
            >
              {rows.map((_, i) => (
                <Cell key={i} fill={colors[i % colors.length]} />
              ))}
            </Pie>
            <Tooltip
              formatter={(v, n) => [`${fmtNum(v)} (${((Number(v) / total) * 100).toFixed(1)}%)`, n]}
              contentStyle={{
                borderRadius: 6, border: `1px solid ${C.grid}`, fontSize: 11,
                padding: '6px 8px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
              }}
            />
          </PieChart>
        </ResponsiveContainer>
        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
          <span className="text-base font-semibold text-gray-900 tabular-nums leading-none">
            {centerValue ?? fmtNum(total)}
          </span>
          {centerLabel && (
            <span className="text-[10px] text-gray-400 mt-0.5">{centerLabel}</span>
          )}
        </div>
      </div>

      {/* Legend column — scrolls if there are many slices */}
      <div className="flex-1 min-w-0 space-y-1.5 max-h-[132px] overflow-y-auto pr-1">
        {rows.map((r, i) => {
          const pct = ((Number(r.value) || 0) / total) * 100;
          return (
            <div key={`${r.label}-${i}`} className="flex items-center gap-2 min-w-0">
              <span
                className="w-2 h-2 rounded-full flex-shrink-0"
                style={{ backgroundColor: colors[i % colors.length] }}
              />
              <span className="text-xs text-gray-600 truncate min-w-0 flex-1" title={r.title || r.label}>
                {r.label}
              </span>
              <span className="text-xs text-gray-900 font-medium tabular-nums flex-shrink-0">
                {pct.toFixed(0)}%
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ── Main component ───────────────────────────────────────────
const WebTrackingOverview = ({ analytics, loading, onRangeChange }) => {
  const [range, setRange] = useState(30);
  const [modal, setModal] = useState(null); // { title, icon, rows }

  const handleRange = (days) => {
    setRange(days);
    if (onRangeChange) onRangeChange(days);
  };

  // Normalize every list into { label, value, title } so the UI never
  // has to guess at shapes, and unknown/empty values are never invented.
  const data = useMemo(() => {
    const a = analytics || {};
    const arr = (v) => (Array.isArray(v) ? v : []);

    const daily = arr(a.daily_visits).map((d) => ({
      date: d?.date ?? null,
      visits: Number(d?.visits) || 0,
      visitors: Number(d?.visitors) || 0,
      identified: Number(d?.identified) || 0,
    }));

    const pages = arr(a.top_pages)
      .map((p) => ({
        label: prettyPath(p?.page_url),
        title: safeStr(p?.page_url) || undefined,
        value: Number(p?.views) || 0,
      }))
      .sort((x, y) => y.value - x.value);

    const countries = arr(a.country_breakdown)
      .map((c) => ({
        label: countryName(c?.country),
        title: safeStr(c?.country) ? `${countryName(c.country)} (${safeStr(c.country).toUpperCase()})` : undefined,
        value: Number(c?.visits) || 0,
      }))
      .sort((x, y) => y.value - x.value);

    const devices = arr(a.device_breakdown)
      .map((d) => ({
        label: safeStr(d?.device_type) || 'Unknown',
        key: safeStr(d?.device_type).toLowerCase(),
        value: Number(d?.visits) || 0,
      }))
      .sort((x, y) => y.value - x.value);

    const sources = arr(a.referrer_breakdown)
      .map((r) => ({
        label: prettySource(r?.source),
        title: safeStr(r?.source) || undefined,
        value: Number(r?.visits) || 0,
      }))
      .sort((x, y) => y.value - x.value);

    const campaigns = arr(a.campaign_attribution)
      .map((c) => ({
        label: safeStr(c?.campaign_name) || 'Unattributed',
        title: safeStr(c?.campaign_name) || undefined,
        value: Number(c?.visits) || 0,
      }))
      .sort((x, y) => y.value - x.value);

    const identified = Number(a.identified_visitors) || 0;
    const anonymous = Number(a.anonymous_visitors) || 0;
    const mix = [
      { label: 'Identified', value: identified },
      { label: 'Anonymous', value: anonymous },
    ].filter((d) => d.value > 0);

    return {
      daily, pages, countries, devices, sources, campaigns, mix,
      identified, anonymous,
      totalVisits: Number(a.total_visits) || 0,
      uniqueVisitors: Number(a.unique_visitors) || 0,
      scoreAwarded: Number(a.total_score_awarded) || 0,
      hasAny:
        daily.length > 0 || pages.length > 0 || countries.length > 0 ||
        devices.length > 0 || sources.length > 0 || campaigns.length > 0 ||
        identified > 0 || anonymous > 0,
    };
  }, [analytics]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
        <span className="ml-2.5 text-sm text-gray-500">Loading analytics…</span>
      </div>
    );
  }

  if (!analytics || !data.hasAny) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg py-20 text-center">
        <BarChart3 className="w-10 h-10 text-gray-300 mx-auto mb-3" strokeWidth={1.5} />
        <p className="text-sm font-medium text-gray-600">No visit data for this period</p>
        <p className="text-xs text-gray-400 mt-1">
          Analytics appear once the tracking snippet records its first visit.
        </p>
      </div>
    );
  }

  const identifiedRate = data.totalVisits > 0
    ? ((data.identified / (data.identified + data.anonymous || 1)) * 100).toFixed(0)
    : null;

  return (
    <div className="space-y-4">
      {/* Header + range selector */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h3 className="text-base font-semibold text-gray-900">Overview</h3>
        <div className="inline-flex bg-gray-100 rounded-md p-0.5">
          {RANGE_OPTIONS.map((opt) => (
            <button
              key={opt.days}
              onClick={() => handleRange(opt.days)}
              className={`px-2.5 py-1 text-xs font-medium rounded transition-colors ${
                range === opt.days
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard icon={Eye} label="Total Visits" value={fmtNum(data.totalVisits)} tone="primary" />
        <StatCard icon={Users} label="Unique Visitors" value={fmtNum(data.uniqueVisitors)} tone="gray" />
        <StatCard
          icon={UserCheck}
          label="Identified"
          value={fmtNum(data.identified)}
          tone="success"
          hint={identifiedRate !== null ? `${identifiedRate}% of visitors` : undefined}
        />
        <StatCard
          icon={TrendingUp}
          label="Score Awarded"
          value={`+${fmtNum(data.scoreAwarded)}`}
          tone="warning"
        />
      </div>

      {/* Visitors over time — fixed 260px plot area, never shrinks */}
      {data.daily.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg">
          <div className="px-4 py-3 border-b border-gray-100">
            <h4 className="text-sm font-semibold text-gray-800">Visitors Over Time</h4>
          </div>
          <div className="p-4 pt-2">
            <div style={{ width: '100%', height: 260 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={data.daily} margin={{ top: 8, right: 12, bottom: 4, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={C.grid} vertical={false} />
                  <XAxis
                    dataKey="date"
                    tickFormatter={fmtDateShort}
                    tick={{ fontSize: 11, fill: C.axis }}
                    tickLine={false}
                    axisLine={{ stroke: '#e5e7eb' }}
                    minTickGap={24}
                    height={24}
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: C.axis }}
                    tickLine={false}
                    axisLine={false}
                    width={36}
                    allowDecimals={false}
                  />
                  <Tooltip
                    labelFormatter={fmtDateLong}
                    formatter={(v, n) => [fmtNum(v), n]}
                    contentStyle={{
                      borderRadius: 6, border: `1px solid ${C.grid}`, fontSize: 11,
                      padding: '6px 10px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                    }}
                  />
                  <Legend
                    verticalAlign="top"
                    align="right"
                    height={28}
                    iconType="circle"
                    iconSize={7}
                    wrapperStyle={{ fontSize: 11, paddingBottom: 4 }}
                  />
                  <Line
                    type="monotone" dataKey="visits" name="Visits"
                    stroke={C.primary} strokeWidth={2} dot={false}
                    activeDot={{ r: 3.5, strokeWidth: 0 }}
                  />
                  <Line
                    type="monotone" dataKey="visitors" name="Unique"
                    stroke={C.success} strokeWidth={2} dot={false}
                    activeDot={{ r: 3.5, strokeWidth: 0 }}
                  />
                  <Line
                    type="monotone" dataKey="identified" name="Identified"
                    stroke={C.warning} strokeWidth={2} dot={false}
                    activeDot={{ r: 3.5, strokeWidth: 0 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}

      {/* Breakdowns — 2-up on large screens, stacked on small */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Top Pages */}
        {data.pages.length > 0 && (
          <Card
            title="Top Pages"
            icon={FileText}
            count={data.pages.length}
            onViewAll={data.pages.length > INLINE_ROWS
              ? () => setModal({ title: 'Top Pages', icon: FileText, rows: data.pages })
              : undefined}
          >
            <BarRows rows={data.pages.slice(0, INLINE_ROWS)} color={C.primary} />
          </Card>
        )}

        {/* Visitor mix */}
        {data.mix.length > 0 && (
          <Card title="Visitor Mix" icon={Users}>
            <Donut
              rows={data.mix}
              colors={[C.success, C.grayLight]}
              centerLabel="visitors"
            />
          </Card>
        )}

        {/* Traffic sources */}
        {data.sources.length > 0 && (
          <Card
            title="Traffic Sources"
            icon={Share2}
            count={data.sources.length}
            onViewAll={data.sources.length > INLINE_ROWS
              ? () => setModal({ title: 'Traffic Sources', icon: Share2, rows: data.sources })
              : undefined}
          >
            <Donut rows={data.sources.slice(0, INLINE_ROWS)} centerLabel="visits" />
          </Card>
        )}

        {/* Countries — full names, not codes */}
        {data.countries.length > 0 && (
          <Card
            title="Countries"
            icon={Globe}
            count={data.countries.length}
            onViewAll={data.countries.length > INLINE_ROWS
              ? () => setModal({ title: 'Countries', icon: Globe, rows: data.countries })
              : undefined}
          >
            <BarRows rows={data.countries.slice(0, INLINE_ROWS)} color={C.primaryDark} />
          </Card>
        )}

        {/* Devices */}
        {data.devices.length > 0 && (
          <Card title="Devices" icon={Monitor}>
            <div className="space-y-3">
              {data.devices.map((d, i) => {
                const total = data.devices.reduce((s, v) => s + v.value, 0) || 1;
                const pct = (d.value / total) * 100;
                const Icon = DEVICE_ICONS[d.key] || Monitor;
                return (
                  <div key={`${d.label}-${i}`} className="flex items-center gap-3 min-w-0">
                    <Icon className="w-4 h-4 text-gray-400 flex-shrink-0" strokeWidth={2} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline justify-between gap-3 mb-1">
                        <span className="text-xs text-gray-700 capitalize truncate">{d.label}</span>
                        <span className="text-xs text-gray-500 tabular-nums flex-shrink-0">
                          {fmtNum(d.value)}
                          <span className="text-gray-400 ml-1">{pct.toFixed(0)}%</span>
                        </span>
                      </div>
                      <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className="h-full rounded-full transition-all duration-300"
                          style={{ width: `${pct}%`, backgroundColor: SERIES[i % SERIES.length] }}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        )}

        {/* Campaign attribution */}
        {data.campaigns.length > 0 && (
          <Card
            title="Campaign Attribution"
            icon={Target}
            count={data.campaigns.length}
            onViewAll={data.campaigns.length > INLINE_ROWS
              ? () => setModal({ title: 'Campaign Attribution', icon: Target, rows: data.campaigns })
              : undefined}
          >
            <BarRows rows={data.campaigns.slice(0, INLINE_ROWS)} color={C.warning} />
          </Card>
        )}
      </div>

      {/* Overflow modal for long lists */}
      {modal && (
        <OverflowModal
          title={modal.title}
          icon={modal.icon}
          rows={modal.rows}
          onClose={() => setModal(null)}
        />
      )}
    </div>
  );
};

export default WebTrackingOverview;
